import { useEffect, useState, useCallback } from "react";
import {
  Loader2, GraduationCap, Users, ChevronRight, ArrowLeft, Plus, Trash2,
  Check, Circle, PlayCircle, Save, Edit2, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";

const STATUS_META = {
  completed: { label: "Completato", color: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10", icon: Check },
  in_progress: { label: "In corso", color: "text-sheriff-gold border-sheriff-gold/40 bg-sheriff-gold/10", icon: PlayCircle },
  pending: { label: "In attesa", color: "text-sheriff-gold/50 border-sheriff-gold/25 bg-[#143830]", icon: Circle },
};

export default function HRFormazionePanel() {
  const [tab, setTab] = useState("allievi"); // "allievi" | "steps"
  const [selectedUserId, setSelectedUserId] = useState(null);

  if (selectedUserId) {
    return (
      <AllievoDetail
        userId={selectedUserId}
        onBack={() => setSelectedUserId(null)}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="hr-formazione">
      <div className="flex gap-2 border-b border-sheriff-gold/20">
        <TabButton active={tab === "allievi"} onClick={() => setTab("allievi")} data-testid="tab-allievi">
          <Users className="h-4 w-4" /> Allievi
        </TabButton>
        <TabButton active={tab === "steps"} onClick={() => setTab("steps")} data-testid="tab-steps">
          <GraduationCap className="h-4 w-4" /> Step Formativi
        </TabButton>
      </div>
      {tab === "allievi" ? (
        <AllieviList onSelect={setSelectedUserId} />
      ) : (
        <StepsManager />
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, ...rest }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 flex items-center gap-2 uppercase-label text-xs tracking-wider transition-colors border-b-2 -mb-[2px] ${
        active ? "border-sheriff-gold text-sheriff-gold" : "border-transparent text-[#8B7B49] hover:text-sheriff-gold/80"
      }`}
      {...rest}
    >
      {children}
    </button>
  );
}

function AllieviList({ onSelect }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/training/allievi").then(({ data }) => setList(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-sheriff-gold" /></div>;
  if (!list || !list.length) {
    return <div className="rounded-xl border border-dashed border-sheriff-gold/20 p-10 text-center text-sheriff-gold/60">Nessun allievo attualmente attivo.</div>;
  }
  return (
    <div className="space-y-2" data-testid="allievi-list">
      {list.map((a) => (
        <button
          key={a.user_id}
          onClick={() => onSelect(a.user_id)}
          className="w-full text-left rounded-xl border border-sheriff-gold/20 hover:border-sheriff-gold/60 bg-gradient-to-br from-[#143830] to-[#0F2620] px-4 py-3 flex items-center gap-4 group transition-all hover:-translate-y-0.5"
          data-testid={`allievo-row-${a.user_id}`}
        >
          <div className="min-w-0 flex-1">
            <div className="font-medium text-[#F5E6B8] group-hover:text-sheriff-gold truncate">{a.rp_name || a.email}</div>
            <div className="dossier-mono text-[10px] uppercase tracking-wider text-sheriff-gold/60 mt-0.5">
              {a.badge_number ? `Matricola ${a.badge_number}` : "— matricola non assegnata"}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-heading text-2xl text-sheriff-gold leading-none">{a.percent}%</div>
            <div className="text-[10px] uppercase tracking-wider text-sheriff-gold/50 mt-1">
              {a.completed_steps}/{a.steps_total} step
            </div>
          </div>
          <div className="w-40 h-2 rounded-full bg-[#0A1F18] overflow-hidden shrink-0">
            <div className="h-full bg-gradient-to-r from-sheriff-gold/70 to-sheriff-gold transition-all"
              style={{ width: `${a.percent}%` }} />
          </div>
          <ChevronRight className="h-5 w-5 text-sheriff-gold/40 group-hover:text-sheriff-gold group-hover:translate-x-1 transition-all shrink-0" />
        </button>
      ))}
    </div>
  );
}

function AllievoDetail({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingStep, setEditingStep] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/training/progress/${userId}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-sheriff-gold" /></div>;
  if (!data) return <div className="text-sheriff-gold/60">Errore caricamento dati allievo.</div>;

  return (
    <div className="space-y-5" data-testid="allievo-detail">
      <div className="flex items-center gap-3">
        <Button onClick={onBack} variant="outline" size="sm" data-testid="back-to-allievi">
          <ArrowLeft className="h-4 w-4 mr-1" /> Allievi
        </Button>
      </div>
      <div className="rounded-2xl border border-sheriff-gold/30 bg-gradient-to-br from-sheriff-gold/10 to-[#0F2620] p-6">
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-heading text-2xl text-[#F5E6B8] tracking-tight">{data.user.rp_name || data.user.email}</h2>
            <div className="text-xs uppercase-label text-sheriff-gold/60 mt-1">
              {data.user.role} {data.user.badge_number && `· Matricola ${data.user.badge_number}`}
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading text-4xl text-sheriff-gold">{data.percent}%</div>
            <div className="text-xs text-[#D4C480]">{data.completed_steps}/{data.total_steps} step</div>
          </div>
        </div>
        <div className="mt-4 h-2 w-full bg-[#0A1F18] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-sheriff-gold/70 to-sheriff-gold transition-all"
            style={{ width: `${data.percent}%` }} />
        </div>
      </div>

      <div className="space-y-2">
        {data.timeline.map((step, idx) => {
          const meta = STATUS_META[step.status] || STATUS_META.pending;
          const Icon = meta.icon;
          return (
            <div key={step.id} className="rounded-xl border border-sheriff-gold/20 bg-[#143830]/40 p-4"
              data-testid={`hr-step-${step.order}`}>
              <div className="flex items-start gap-4">
                <div className={`h-9 w-9 rounded-full border-2 flex items-center justify-center shrink-0 ${meta.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-3 flex-wrap mb-0.5">
                    <span className="dossier-mono text-[10px] uppercase tracking-[0.2em] text-sheriff-gold/50">
                      Step {String(idx + 1).padStart(2, "0")}
                    </span>
                    {step.category && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sheriff-gold/10 border border-sheriff-gold/30 text-sheriff-gold/70">
                        {step.category}
                      </span>
                    )}
                    <span className={`text-[10px] uppercase tracking-wider ml-auto px-2 py-0.5 rounded border ${meta.color}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-[#F5E6B8] font-medium">{step.name}</div>
                  {step.description && <div className="text-xs text-[#D4C480] mt-0.5">{step.description}</div>}
                  {step.note && (
                    <div className="mt-2 text-xs bg-sheriff-gold/[0.04] border-l-2 border-sheriff-gold/40 pl-2 py-1 text-[#D4C480]">
                      <span className="dossier-mono text-[10px] uppercase tracking-wider text-sheriff-gold/60">
                        Nota{step.updated_by_name ? ` · ${step.updated_by_name}` : ""}:{" "}
                      </span>
                      {step.note}
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditingStep(step)}
                  data-testid={`hr-edit-step-${step.order}`}
                >
                  <Edit2 className="h-3.5 w-3.5 mr-1" /> Aggiorna
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {editingStep && (
        <StepUpdateDialog
          step={editingStep}
          userId={userId}
          onClose={() => setEditingStep(null)}
          onSaved={() => { setEditingStep(null); load(); }}
        />
      )}
    </div>
  );
}

function StepUpdateDialog({ step, userId, onClose, onSaved }) {
  const [status, setStatus] = useState(step.status || "pending");
  const [note, setNote] = useState(step.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/training/progress/${userId}/${step.id}`, { status, note });
      toast.success("Step aggiornato");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento fallito");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Aggiorna step</DialogTitle>
          <DialogDescription>{step.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="uppercase-label text-xs text-sheriff-gold mb-1 block">Stato</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="step-status-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">In attesa</SelectItem>
                <SelectItem value="in_progress">In corso</SelectItem>
                <SelectItem value="completed">Completato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="uppercase-label text-xs text-sheriff-gold mb-1 block">Nota (opzionale)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Commento HR visibile all'allievo" data-testid="step-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="step-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepsManager() {
  const [steps, setSteps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // step object or "new"

  const load = useCallback(() => {
    setLoading(true);
    api.get("/training/steps").then(({ data }) => setSteps(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (stepId) => {
    if (!confirm("Eliminare questo step? Verranno rimossi anche i progressi collegati.")) return;
    try {
      await api.delete(`/training/steps/${stepId}`);
      toast.success("Step eliminato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eliminazione fallita"); }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-sheriff-gold" /></div>;

  return (
    <div className="space-y-3" data-testid="steps-manager">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#D4C480]">
          {steps?.length || 0} step configurati per il percorso Allievo → Vice Sceriffo
        </div>
        <Button onClick={() => setEditing("new")} data-testid="add-step-btn">
          <Plus className="h-4 w-4 mr-1" /> Nuovo step
        </Button>
      </div>
      <div className="space-y-2">
        {steps.map((s) => (
          <div key={s.id} className="rounded-xl border border-sheriff-gold/20 bg-[#143830]/40 p-4 flex items-start gap-3" data-testid={`step-row-${s.id}`}>
            <div className="h-9 w-9 rounded-full border-2 border-sheriff-gold/40 bg-[#0F2620] flex items-center justify-center dossier-mono text-sheriff-gold text-sm shrink-0">
              {s.order}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <div className="text-[#F5E6B8] font-medium">{s.name}</div>
                {s.category && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sheriff-gold/10 border border-sheriff-gold/30 text-sheriff-gold/70">
                    {s.category}
                  </span>
                )}
                {!s.mandatory && (
                  <span className="text-[10px] uppercase tracking-wider text-sheriff-gold/50">Opzionale</span>
                )}
              </div>
              {s.description && <div className="text-xs text-[#D4C480] mt-0.5">{s.description}</div>}
            </div>
            <Button size="sm" variant="outline" onClick={() => setEditing(s)} data-testid={`edit-step-${s.id}`}>
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => del(s.id)} data-testid={`del-step-${s.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
      {editing && (
        <StepEditDialog
          step={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function StepEditDialog({ step, onClose, onSaved }) {
  const isNew = !step;
  const [name, setName] = useState(step?.name || "");
  const [description, setDescription] = useState(step?.description || "");
  const [category, setCategory] = useState(step?.category || "");
  const [order, setOrder] = useState(step?.order ?? 10);
  const [mandatory, setMandatory] = useState(step?.mandatory ?? true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Nome obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description, category, order: Number(order), mandatory };
      if (isNew) await api.post("/training/steps", payload);
      else await api.patch(`/training/steps/${step.id}`, payload);
      toast.success(isNew ? "Step creato" : "Step aggiornato");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nuovo step" : "Modifica step"}</DialogTitle>
          <DialogDescription>Definisci un passo del percorso formativo Allievo → Vice Sceriffo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="uppercase-label text-xs text-sheriff-gold mb-1 block">Ordine</label>
              <Input type="number" value={order} onChange={(e) => setOrder(e.target.value)} data-testid="step-order" />
            </div>
            <div className="col-span-2">
              <label className="uppercase-label text-xs text-sheriff-gold mb-1 block">Categoria</label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Teoria / Pratica / Onboarding..." data-testid="step-category" />
            </div>
          </div>
          <div>
            <label className="uppercase-label text-xs text-sheriff-gold mb-1 block">Nome</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Colloquio Ingresso" data-testid="step-name" />
          </div>
          <div>
            <label className="uppercase-label text-xs text-sheriff-gold mb-1 block">Descrizione</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="step-description" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#D4C480] cursor-pointer select-none">
            <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />
            Step obbligatorio (deve essere completato per la promozione)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1" />Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="step-form-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
